import DOMPurify from "dompurify";

/** Tags that behave reasonably across common email clients for simple marketing copy. */
const EMAIL_ALLOWED_TAGS = ["p", "br", "strong", "b", "em", "i", "a", "ul", "ol", "li", "span", "img"];

const EMAIL_ALLOWED_ATTR = ["href", "target", "rel", "src", "alt", "width", "height", "title", "style"];

/** Only http(s) image URLs — matches typical hosted assets for email. */
const EMAIL_IMG_SRC_SAFE = /^https?:\/\//i;

/** TipTap / email: paragraph text-align only (no arbitrary CSS). */
const PARAGRAPH_TEXT_ALIGN_STYLE = /^text-align:\s*(left|right|center|justify)\s*(;\s*)?$/i;

let domPurifyStyleHookInstalled = false;

function ensureEmailStyleAttributeHook(): void {
  if (domPurifyStyleHookInstalled) return;
  domPurifyStyleHookInstalled = true;
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName !== "style") return;
    const tag = (node as Element).tagName;
    if (tag === "P") {
      const v = data.attrValue.replace(/\s+/g, " ").trim();
      if (!PARAGRAPH_TEXT_ALIGN_STYLE.test(v)) {
        data.keepAttr = false;
      }
      return;
    }
    if (tag === "IMG") {
      data.keepAttr = false;
      return;
    }
    data.keepAttr = false;
  });
}

/**
 * Sanitizes HTML for notification email bodies: safe subset for storage and sending.
 */
export function sanitizeEmailHtml(html: string): string {
  ensureEmailStyleAttributeHook();
  const dirty = html ?? "";
  const clean = DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: EMAIL_ALLOWED_TAGS,
    ALLOWED_ATTR: EMAIL_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });

  const doc = new DOMParser().parseFromString(clean, "text/html");
  doc.querySelectorAll("img").forEach((el) => {
    const src = el.getAttribute("src")?.trim() ?? "";
    if (!src || !EMAIL_IMG_SRC_SAFE.test(src)) {
      el.remove();
      return;
    }
    el.setAttribute("style", "max-width:100%;height:auto;display:block");
  });
  return doc.body.innerHTML.trim();
}
